import { ClientData } from '../types/ClientData'

/**
 * Generate a Life Design Statement — max 4 sentences, personal and specific.
 *
 * Rules:
 * 1. Open with the person, not the plan. Name + something specific (age, business, freedom age).
 * 2. Reference at least 3 specific numbers from ClientData.
 * 3. Name the committed scenarios and what they mean.
 * 4. Close in second person, present tense — active, not passive.
 * 5. Max 4 sentences. No filler: "journey", "holistic", "going forward", "roadmap", "empower".
 */
export function generateLifeDesignStatement(d: ClientData): string {
  const firstName = (d.clientName || 'Your client').split(' ')[0]
  const partner = d.isCouple && d.partnerName ? ` and ${d.partnerName}` : ''
  const age = d.clientAge || 0
  const freedomAge = d.s3FinalFreedomAge || d.targetFreedomAge || 0
  const yearsToFreedom = freedomAge > age ? freedomAge - age : 0
  const freedomNumber = d.freedomNumberInflated || d.freedomNumberToday || 0
  const income = d.rlsaAnnualTarget || 0
  const pensionMonthly = d.pensionMonthly || 0
  const doNothingAge = d.voyantDoNothing?.retirementAge || 0

  // ── Sentence 1: Open with the person ─────────────────────────────────────
  let s1: string
  if (d.businessName && yearsToFreedom > 0) {
    s1 = `${firstName}${partner}, at ${age}, running ${d.businessName} — in ${yearsToFreedom} years you will be financially free at ${freedomAge}.`
  } else if (yearsToFreedom > 0) {
    s1 = `${firstName}${partner}, at ${age} — in ${yearsToFreedom} years you will be financially free at ${freedomAge}.`
  } else {
    s1 = `${firstName}${partner}, your freedom age is ${freedomAge}.`
  }

  // ── Sentence 2: The numbers that make it real ────────────────────────────
  const numberParts: string[] = []
  if (freedomNumber > 0) numberParts.push(`a freedom fund of £${freedomNumber.toLocaleString()}`)
  if (income > 0) numberParts.push(`£${income.toLocaleString()} per year in today's money`)
  if (pensionMonthly > 0) numberParts.push(`£${pensionMonthly.toLocaleString()}/month into your pension`)

  let s2 = ''
  if (numberParts.length >= 2) {
    s2 = ` That means ${numberParts.join(', ')}.`
  } else if (numberParts.length === 1) {
    s2 = ` That means ${numberParts[0]}.`
  }

  // ── Sentence 3: Committed scenarios ──────────────────────────────────────
  const scenarioParts: string[] = []
  const committed = d.s3ScenariosCommitted || []

  if (committed.includes('earlier') && d.s3EarlierFreedomAge > 0) {
    const gained = doNothingAge > 0 ? doNothingAge - d.s3EarlierFreedomAge : 0
    scenarioParts.push(gained > 0
      ? `retiring ${gained} years earlier at ${d.s3EarlierFreedomAge}`
      : `an earlier target of ${d.s3EarlierFreedomAge}`)
  }

  if (committed.includes('phased') && d.s3PhasedStartAge > 0) {
    scenarioParts.push(`a phased bridge from ${d.s3PhasedStartAge} at £${(d.s3PhasedMonthlyIncome || 0).toLocaleString()}/month`)
  }

  if (committed.includes('lifestyle') && d.s3LifestyleBusinessMonthlyIncome > 0) {
    scenarioParts.push(`keeping the business as a lifestyle venture at £${(d.s3LifestyleBusinessMonthlyIncome * 12).toLocaleString()}/year`)
  }

  if (committed.includes('oneoff') && d.s3OneOffDescription) {
    scenarioParts.push(`${d.s3OneOffDescription} of £${(d.s3OneOffAmount || 0).toLocaleString()} at age ${d.s3OneOffAtAge}`)
  }

  let s3 = ''
  if (scenarioParts.length > 0) {
    s3 = ` You have chosen ${scenarioParts.join(', ')}.`
  }

  // ── Sentence 4: Forward-looking close in second person, present tense ───
  const total = (pensionMonthly) + (d.isaContribution || 0) + (d.mortgageOP || 0)
  let s4: string
  if (total > 0 && yearsToFreedom > 0) {
    s4 = ` You are building this with £${total.toLocaleString()}/month in committed actions — every month moves you closer.`
  } else if (yearsToFreedom > 0) {
    s4 = ` You are ${yearsToFreedom} years from freedom, and the plan is in motion.`
  } else {
    s4 = ` The plan is set — you are in control of what comes next.`
  }

  return (s1 + s2 + s3 + s4).trim()
}
